<template>
	<Accessible
		name="pinmodal"
		trapped
		focusFirstItem
		autoFocus
		:wait="100"
		:tabOrder="[
			{ css: '.modal-close-btn', clickOnEnter: true },
			{ all: '.option', clickOnEnter: true },
		]"
	>
		<SimpleModal
			@close="close"
			v-if="(!existingPin || confirmUnpin) && !showDatePicker"
		>
			<div class="pin-modal-wrapper">
				<div
					class="title"
					v-text="t('chat_pin_message_expiration_date_picker_title')"
				/>
				<div
					class="desc"
					v-text="t('chat_pin_message_expiration_date_picker_description')"
				/>
				<div
					v-for="{ id, label } in options"
					:key="id"
					@click="selected(id)"
					class="option"
					:class="{ [`option-${id}`]: true }"
				>
					<Spinner v-if="loading && selectedOption === id" />
					<span v-else v-text="label" />
				</div>
			</div>
		</SimpleModal>

		<StandardModal
			v-if="existingPin && !confirmUnpin && !showDatePicker"
			open
			:title="t('chat_pin_message_confirmation_title')"
			:confirmText="t('chat_pin_message_confirmation_confirm')"
			@confirm="confirm"
			@cancel="close"
			small
			ref="modal"
		>
			<div class="unpin-modal-wrapper">
				<ThumbtackIcon halo class="pin-icon" type="regular" />
				<div
					class="help"
					v-text="t('chat_pin_message_confirmation_description')"
				/>
			</div>
		</StandardModal>

		<!-- SCHEDULED DATE PICKER -->
		<DatePickerModal
			v-if="showDatePicker"
			v-model="customDate"
			noPastDates
			:confirmText="t('search_date_filter_setdate')"
			:cancelText="t('common_button_cancel')"
			@cancel="showDatePicker = false"
			@close="showDatePicker = false"
		/>
	</Accessible>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import store, { getChat } from '@/store'
import {
	SimpleModal,
	Spinner,
	StandardModal,
	DatePickerModal,
	useAlert,
} from '@/components/common'
import { ThumbtackIcon } from '@/icons'
import moment from 'moment'
import Accessible from '@/components/common/Accessible'
import { useI18n } from 'vue-i18n'
import { updateChat } from '@/api/chat'

const props = defineProps(['messageId'])
const emit = defineEmits(['close'])
const { t } = useI18n()
const alert = useAlert()
const confirmUnpin = ref(false)
const success = ref(false)
const selectedOption = ref(null)
const loading = ref(false)
const showDatePicker = ref(false)
const customDate = ref(null)

const existingPin = computed(() => {
	return getChat(store.currentChatId).pinned_message_id
})

const options = computed(() => [
	{
		id: '1D',
		label: t(
			'accessibility_days_label.variable',
			{
				Count: 1,
			},
			1
		),
	},
	{
		id: '7D',
		label: t(
			'accessibility_days_label.variable',
			{
				Count: 7,
			},
			7
		),
	},
	{
		id: '30D',
		label: t(
			'accessibility_days_label.variable',
			{
				Count: 30,
			},
			30
		),
	},
	{
		id: 'CUSTOM',
		label: t('groupinvites_expire_custom'),
	},
])

watch(customDate, (date) => {
	if (!date) return
	// convert our date from the calendar to a moment object
	const selectedDate = moment(date)
	// get our current time
	const currentTime = moment()
	// set the time of our calendar date to the current time
	selectedDate
		.hour(currentTime.hour())
		.minute(currentTime.minute())
		.second(currentTime.second())
	pinMessage(selectedDate.toDate())
})

const close = () => {
	emit('close', { success: success.value })
}

const selected = async (option) => {
	selectedOption.value = option
	loading.value = true
	if (option === 'CUSTOM') {
		showDatePicker.value = true
	} else {
		const days = option.slice(0, -1)
		const expiration = moment().add(days, 'days')
		pinMessage(expiration.toDate())
	}
}

const confirm = () => {
	confirmUnpin.value = true
}

const pinMessage = async (exp) => {
	try {
		await updateChat(store.currentChatId, {
			pinned_message_id: props.messageId,
			pinned_message_expires_at: exp,
		})
		loading.value = false
	} catch (e) {
		alert.error(t('chat_pin_message_pin_failure_title'))
	} finally {
		close()
	}
}
</script>

<style lang="scss" scoped>
@import 'src/sass/variables';

.pin-modal-wrapper {
	width: 390px;
	text-align: center;
	color: var(--neutral-800);

	.title {
		margin-top: 20px;
		margin-bottom: 24px;
		font-family: Avenir-Heavy;
		font-size: 20px;
		padding: 0 60px;
	}
	.desc {
		border-top: solid 1px var(--neutral-40040);
		font-size: 14px;
		color: var(--neutral-600);
		padding: 16px 50px;
		font-family: Avenir-Roman;
	}
	.option {
		cursor: pointer;
		@include flex-center;
		height: 52px;
		border-top: solid 1px var(--neutral-40040);
		&:not(.option-0):hover {
			color: var(--blue-300);
			cursor: pointer;
		}
		&.option-0 {
			color: var(--red-300);
		}
	}
}

.unpin-modal-wrapper {
	@include flex-column-center;
	.pin-icon {
		color: var(--blue-300);
		font-size: 58px;
	}

	.pin-icon,
	.help {
		margin-top: 24px;
	}

	.help {
		font-family: Avenir-Medium;
		color: var(--neutral-800);
		max-width: 400px;
		font-size: 18px;
		text-align: center;
	}
}
</style>
